// Web Push browser glue: ask permission, subscribe with the server's VAPID
// key, hand the subscription back to /api/push/subscribe. Designed to be
// idempotent — calling enablePush() twice on the same device is a no-op
// after the first success.

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export interface PushState {
  /** Browser supports Notification + PushManager + ServiceWorker. */
  supported: boolean;
  /** Permission state — 'granted' | 'denied' | 'default'. */
  permission: NotificationPermission;
  /** True if we have an active push subscription on this device. */
  subscribed: boolean;
  /** Only relevant on iOS Safari: true if the user must add to home screen first. */
  needsInstall: boolean;
}

export function readPushState(): PushState {
  const supported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  return {
    supported,
    permission: supported ? Notification.permission : 'denied',
    subscribed: false, // updated after async check
    needsInstall: isIos && !isStandalone,
  };
}

/** Returns the currently-active push subscription on this device, or null. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/** Ask permission + subscribe + register with the server. Resolves to true
 *  iff the device is now receiving pushes. */
export async function enablePush(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;

  // Step 1: ask for permission. iOS only honours this from a user gesture.
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return false;

  // Step 2: fetch the VAPID public key.
  const keyRes = await fetch('/api/push/key', { credentials: 'include' });
  if (!keyRes.ok) return false;
  const { key } = (await keyRes.json()) as { key: string | null };
  if (!key) return false;

  // Step 3: subscribe via the service worker's push manager.
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Uint8Array is a valid BufferSource; the cast satisfies the stricter
      // ArrayBufferView<ArrayBuffer> typing in recent lib.dom.
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    });
  }

  // Step 4: register the subscription with the server.
  const json = sub.toJSON();
  const res = await fetch('/api/push/subscribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      userAgent: navigator.userAgent,
    }),
  });
  return res.ok;
}

/** Unsubscribe locally + on the server. */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await fetch('/api/push/unsubscribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  });
  await sub.unsubscribe();
}
